import { BRIEF_STATUS } from "../../enums/brief";
import type { GovernanceState } from "../../enums/govern";
import {
  GOVERN_ROLE_KEY,
  GOVERNANCE_STATE,
  VERIFICATION_RESULT,
} from "../../enums/govern";
import { canMarkBriefReady } from "../brief/briefReadiness";
import type { DeliveryBrief } from "../brief/briefTypes";
import type {
  GovernCheck,
  GovernReviewSchedule,
  GovernWorkItem,
} from "./governTypes";

export function sourceBriefReady(brief: DeliveryBrief | null): boolean {
  return Boolean(
    brief?.status === BRIEF_STATUS.READY_FOR_DELIVERY &&
      canMarkBriefReady(brief),
  );
}

export function isAssigned(value: string): boolean {
  const normalized = value.trim().toLocaleLowerCase();
  return (
    normalized.length > 0 &&
    normalized !== "not assigned" &&
    normalized !== "unassigned"
  );
}

export function checkIntegrity(check: GovernCheck): boolean {
  if (check.result === VERIFICATION_RESULT.NOT_CHECKED) return true;
  return hasText(check.evidenceNote) && isAssigned(check.verifiedBy);
}

export function canEditVerification(
  work: GovernWorkItem,
  brief: DeliveryBrief | null,
): boolean {
  return (
    work.state === GOVERNANCE_STATE.READY_FOR_REVIEW && sourceBriefReady(brief)
  );
}

export function canVerifyWork(
  work: GovernWorkItem,
  brief: DeliveryBrief | null,
): boolean {
  return (
    canEditVerification(work, brief) &&
    missingVerificationRoles(work).length === 0 &&
    work.checks.length > 0 &&
    work.checks.every(
      (check) =>
        checkIntegrity(check) &&
        (!check.required ||
          check.result === VERIFICATION_RESULT.PASS ||
          check.result === VERIFICATION_RESULT.NOT_APPLICABLE),
    )
  );
}

export function missingVerificationRoles(work: GovernWorkItem): string[] {
  const roles: Array<[keyof GovernWorkItem["roles"], string]> = [
    [GOVERN_ROLE_KEY.DELIVERY_OWNER, "Delivery owner"],
    [GOVERN_ROLE_KEY.REVIEWER, "Reviewer"],
    [GOVERN_ROLE_KEY.VERIFIER, "Verifier"],
  ];
  return roles
    .filter(([key]) => !isAssigned(work.roles[key]))
    .map(([, label]) => label);
}

export function validReviewSchedule(
  schedule: GovernReviewSchedule | null,
): boolean {
  return Boolean(
    schedule?.nextReviewDate &&
      isAssigned(schedule.reviewOwner) &&
      hasText(schedule.reason) &&
      (schedule.triggers.some(hasText) || hasText(schedule.mustRemainTrue)),
  );
}

export function governanceStateLabel(state: GovernanceState): string {
  const labels: Record<GovernanceState, string> = {
    ready_for_delivery: "Ready for delivery",
    in_delivery: "In delivery",
    ready_for_review: "Ready for review",
    needs_rework: "Needs rework",
    verified: "Verified",
    review_scheduled: "Review scheduled",
  };
  return labels[state];
}

export function verificationSummary(work: GovernWorkItem) {
  const checks = work.checks;
  return {
    total: checks.length,
    passed: checks.filter((check) => check.result === VERIFICATION_RESULT.PASS)
      .length,
    failed: checks.filter((check) => check.result === VERIFICATION_RESULT.FAIL)
      .length,
    notApplicable: checks.filter(
      (check) => check.result === VERIFICATION_RESULT.NOT_APPLICABLE,
    ).length,
    unchecked: checks.filter(
      (check) => check.result === VERIFICATION_RESULT.NOT_CHECKED,
    ).length,
    invalid: checks.filter(
      (check) =>
        check.result !== VERIFICATION_RESULT.NOT_CHECKED &&
        !checkIntegrity(check),
    ).length,
  };
}

function hasText(value: string): boolean {
  return value.trim().length > 0;
}
